/**
 * Date Formatter Utility
 * Formats dates to match Figma design: DD-MM-YY, HH:MM AM/PM
 */

/**
 * Format ISO date string to Figma format: DD-MM-YY, HH:MM AM/PM
 * @param dateString ISO date string (e.g., "2025-11-17T10:03:13.305Z")
 * @returns Formatted date string (e.g., "17-11-25, 10:03 AM")
 */
export function formatDateToFigma(dateString: string | null | undefined): string {
  if (!dateString) {
    return '';
  }

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    // Get date components
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2); // Last 2 digits of year

    // Get time components
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const formattedHours = String(hours).padStart(2, '0');

    // Format: DD-MM-YY, HH:MM AM/PM
    return `${day}-${month}-${year}, ${formattedHours}:${minutes} ${ampm}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

/**
 * Format ISO date string to date only: DD-MM-YY (no time).
 * Use for Created at / Last Modified columns where only date is needed.
 */
export function formatDateOnly(dateString: string | null | undefined): string {
  if (!dateString) {
    return '';
  }
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  } catch {
    return '';
  }
}

/**
 * Format ISO date string as "YYYY-MM-DD HH:mm" (e.g. 2025-01-10 10:30).
 * Use for Last Modified / Created at when time should be shown.
 */
export function formatDateTimeShort(dateString: string | null | undefined): string {
  if (!dateString) {
    return '';
  }
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch {
    return '';
  }
}

const RELATIVE_TIME_UNITS: Array<{ unit: Intl.RelativeTimeFormatUnit; seconds: number }> = [
  { unit: 'year', seconds: 60 * 60 * 24 * 365 },
  { unit: 'month', seconds: 60 * 60 * 24 * 30 },
  { unit: 'week', seconds: 60 * 60 * 24 * 7 },
  { unit: 'day', seconds: 60 * 60 * 24 },
  { unit: 'hour', seconds: 60 * 60 },
  { unit: 'minute', seconds: 60 },
];

/**
 * Format ISO date string as relative time (e.g. "8 days ago", "just now").
 */
export function formatRelativeTimeAgo(dateString: string | null | undefined): string {
  if (!dateString) {
    return '';
  }

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
    const absSeconds = Math.abs(diffSeconds);

    if (absSeconds < 45) {
      return 'just now';
    }

    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    for (const { unit, seconds } of RELATIVE_TIME_UNITS) {
      if (absSeconds >= seconds || unit === 'minute') {
        const value = Math.round(diffSeconds / seconds);
        return rtf.format(value, unit);
      }
    }

    return rtf.format(0, 'second');
  } catch {
    return '';
  }
}
